<template>
  <div class="pm-table-wrap">
    <div class="pm-table-head">
      <h6 class="mb-0">{{ title }}</h6>
      <div class="d-flex align-items-center" style="gap: 6px">
        <b-button-group size="sm">
          <b-button
            :variant="tab === 'partners' ? 'primary' : 'outline-secondary'"
            @click="$emit('update:tab', 'partners')"
          >
            {{ $t('partner-map.table.tab-partners') }}
          </b-button>
          <b-button
            :variant="tab === 'heroes' ? 'primary' : 'outline-secondary'"
            @click="$emit('update:tab', 'heroes')"
          >
            {{ $t('partner-map.table.tab-heroes') }}
          </b-button>
        </b-button-group>
        <b-button size="sm" variant="outline-secondary" @click="exportCsv">
          {{ $t('partner-map.table.csv') }}
        </b-button>
      </div>
    </div>

    <div class="pm-table-scroll">
      <table class="pm-table">
        <thead>
          <tr>
            <th
              v-for="c in columns"
              :key="c.key"
              :class="{ on: c.key === sortKey, num: c.type !== 's' }"
              @click="sortBy(c)"
            >
              {{ $t('partner-map.col.' + (c.labelKey || c.key)) }}
              <span class="arw">{{ arrow(c) }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(o, i) in sortedRows"
            :key="o.id || o.name"
            :class="{ sel: selection && selection.ref === o }"
            @click="
              $emit('select', {
                kind: tab === 'partners' ? 'partner' : 'hero',
                ref: o,
              })
            "
          >
            <td
              v-for="(c, ci) in columns"
              :key="c.key"
              :class="{ num: c.type !== 's' }"
            >
              <template v-if="ci === 0">
                <span class="rank">{{ i + 1 }}.</span>
                {{ o.name }}
                <b-badge v-if="o.internal" variant="secondary">
                  {{ $t('partner-map.badge.internal') }}
                </b-badge>
                <b-badge v-if="o.child_accounts" variant="light">
                  {{ o.child_accounts + 1 }}
                </b-badge>
                <b-badge v-if="o.is_paused" variant="warning">
                  {{ $t('partner-map.badge.paused') }}
                </b-badge>
                <div v-if="tab === 'partners'" class="sub">
                  {{ `${o.postal_code || ''} ${o.city || ''}`.trim() }}
                </div>
                <!-- Billed revenue as a solid bar, backlog hatched onto the same scale. -->
                <span v-if="tab === 'partners'" class="bar">
                  <i :style="{ width: barPct(o.revenue_year) }" />
                  <u
                    :style="{
                      left: barPct(o.revenue_year),
                      width: barPct(o.revenue_wip),
                    }"
                  />
                </span>
              </template>
              <template v-else>{{ cell(o, c) }}</template>
            </td>
          </tr>
          <tr v-if="!sortedRows.length">
            <td :colspan="columns.length" class="text-center text-muted py-3">
              {{ $t('partner-map.table.no-rows') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
const PARTNER_COLUMNS = [
  { key: 'name', type: 's' },
  { key: 'revenue_all', type: 'money' },
  { key: 'revenue_year', type: 'money' },
  { key: 'cost_year', type: 'money' },
  { key: 'margin_year', type: 'money' },
  { key: 'margin_pct_year', type: 'pct' },
  { key: 'jobs_year', type: 'n' },
  { key: 'revenue_wip', type: 'money' },
  { key: 'cost_wip', type: 'money' },
  { key: 'jobs_wip', type: 'n' },
  { key: 'active_jobs_now', type: 'n' },
  { key: 'completed_jobs_total', type: 'n' },
  { key: 'rating_avg', type: 'dec' },
  { key: 'rating_count', type: 'n' },
  { key: 'service_area_km', type: 'n' },
  { key: 'child_accounts', type: 'n' },
];

const HERO_COLUMNS = [
  // `labelKey` overrides the default `col.<key>` lookup where two tabs share a field name.
  { key: 'name', type: 's', labelKey: 'hero_name' },
  { key: 'postal_code', type: 's' },
  { key: 'city', type: 's' },
  { key: 'util_pct', type: 'pct' },
  { key: 'util_jobs', type: 'n' },
  { key: 'util_booked_h', type: 'n' },
  { key: 'util_free_h', type: 'n' },
  { key: 'radius_km', type: 'n' },
  { key: 'gid_account', type: 's' },
];

const TEXT_KEYS = ['name', 'city', 'postal_code', 'gid_account'];

export default {
  name: 'PartnerMapTable',
  props: {
    tab: { type: String, required: true },
    title: { type: String, default: '' },
    partners: { type: Array, required: true },
    heroes: { type: Array, required: true },
    selection: { type: Object, default: null },
    query: { type: String, default: '' },
    fmt: { type: Object, required: true },
  },
  data() {
    return { sortKey: 'revenue_all', sortDir: -1 };
  },
  computed: {
    columns() {
      return this.tab === 'partners' ? PARTNER_COLUMNS : HERO_COLUMNS;
    },
    rows() {
      const src = this.tab === 'partners' ? this.partners : this.heroes;
      if (!this.query) return src;
      const q = this.query.toLowerCase();
      return src.filter(
        (o) =>
          String(o.name || '')
            .toLowerCase()
            .includes(q) ||
          String(o.city || '')
            .toLowerCase()
            .includes(q),
      );
    },
    sortedRows() {
      const { sortKey: k, sortDir: dir } = this;
      return this.rows.slice().sort((a, b) => {
        const av = a[k];
        const bv = b[k];
        // Missing values sink to the bottom regardless of direction.
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number')
          return (av - bv) * dir;
        return String(av).localeCompare(String(bv), this.$i18n.locale) * dir;
      });
    },
    maxBar() {
      return Math.max(...this.partners.map((p) => p.revenue_all || 0), 1);
    },
  },
  watch: {
    tab(next) {
      this.sortKey = next === 'partners' ? 'revenue_all' : 'name';
      this.sortDir = next === 'partners' ? -1 : 1;
    },
  },
  methods: {
    arrow(c) {
      // Kept out of the template: a bare `<` in an interpolation trips the Vue parser.
      if (c.key !== this.sortKey) return '↕';
      return this.sortDir < 0 ? '▼' : '▲';
    },
    sortBy(c) {
      if (this.sortKey === c.key) {
        this.sortDir *= -1;
      } else {
        this.sortKey = c.key;
        this.sortDir = TEXT_KEYS.includes(c.key) ? 1 : -1;
      }
    },
    barPct(v) {
      return `${(Math.max(0, v || 0) / this.maxBar) * 100}%`;
    },
    cell(o, c) {
      const v = o[c.key];
      if (c.type === 'money') return this.fmt.money(v);
      if (c.type === 'pct') return this.fmt.pct(v);
      if (c.type === 'dec') return this.fmt.dec(v);
      if (c.type === 'n') return this.fmt.num(v);
      return v == null || v === '' ? '–' : v;
    },
    exportCsv() {
      const cols = this.columns;
      const header = cols
        .map((c) => this.$t(`partner-map.col.${c.labelKey || c.key}`))
        .join(';');
      const body = this.sortedRows.map((o) =>
        cols
          .map((c) => {
            const v = o[c.key];
            if (v == null) return '';
            // Excel-DE reads ';' as the separator and ',' as the decimal mark.
            return typeof v === 'number'
              ? String(v).replace('.', ',')
              : `"${String(v).replace(/"/g, '""')}"`;
          })
          .join(';'),
      );
      const csv = [header, ...body].join('\n');
      const a = document.createElement('a');
      // BOM so Excel picks up UTF-8 and umlauts survive.
      a.href = URL.createObjectURL(
        new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }),
      );
      a.download = `${
        this.tab === 'partners' ? 'gid-partners' : 's4c-mobile-heroes'
      }.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
  },
};
</script>

<style lang="scss" scoped>
.pm-table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.pm-table-scroll {
  overflow-x: auto;
}

.pm-table {
  width: 100%;
  font-size: 12.5px;
  border-collapse: collapse;

  th {
    position: sticky;
    top: 0;
    background: var(--pm-surface-1);
    font-size: 11px;
    font-weight: 600;
    color: var(--pm-ink-3);
    white-space: nowrap;
    padding: 5px 8px;
    border-bottom: 1px solid var(--pm-border);
    cursor: pointer;
    user-select: none;

    &.on {
      color: var(--pm-ink-1);
    }
    .arw {
      opacity: 0.45;
    }
  }

  td {
    padding: 5px 8px;
    border-bottom: 1px solid var(--pm-border);
    vertical-align: top;
  }

  td.num,
  th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  tbody tr {
    cursor: pointer;
  }
  tbody tr:hover {
    background: var(--pm-surface-2);
  }
  tbody tr.sel {
    background: var(--pm-partner-wash);
  }

  .rank {
    color: var(--pm-ink-3);
    font-variant-numeric: tabular-nums;
  }
  .sub {
    font-size: 11px;
    color: var(--pm-ink-3);
  }
  .bar {
    position: relative;
    display: block;
    height: 4px;
    margin-top: 3px;
    min-width: 90px;
    background: var(--pm-surface-2);
    border-radius: 2px;

    i {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      background: var(--pm-partner);
      border-radius: 2px;
    }
    u {
      position: absolute;
      top: 0;
      bottom: 0;
      background: repeating-linear-gradient(
        45deg,
        var(--pm-partner) 0 2px,
        transparent 2px 4px
      );
      opacity: 0.6;
    }
  }
}
</style>
